//@flow
import React, {useState, useEffect} from 'react';
import {
  makeStyles,
  Typography,
  Box,
  FormControlLabel,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  ButtonGroup,
} from '@material-ui/core';
import {AccessTime, Schedule, Clear, SelectAll} from '@material-ui/icons';
import {colors} from '../../../assets/styles/colors';
import CustomButton from '../../../elements/CustomButton/CustomButton';
import IosSwitch from '../../../elements/IosSwitch/IosSwitch';
import {classNames} from '../../../utils/react';

const useStyles = makeStyles(() => ({
  scheduleContainer: {
    marginTop: '1rem',
  },
  scheduleToolbar: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '0.5rem',
    marginBottom: '1rem',
    backgroundColor: colors.grey50,
    borderRadius: '8px',
  },
  presetButtons: {
    display: 'flex',
    justifyContent: 'center',
    '& .MuiButton-root': {
      fontSize: '0.75rem',
      padding: '4px 8px',
      textTransform: 'none',
    },
  },
  scheduleTable: {
    minWidth: 500,
    '& .MuiTableCell-root': {
      padding: '2px',
      border: `1px solid ${colors.grey300}`,
      textAlign: 'center',
      position: 'relative',
    },
    '& .MuiTableCell-head': {
      backgroundColor: colors.grey100,
      fontWeight: 600,
    },
  },
  timeCell: {
    width: '60px',
    fontSize: '0.75rem',
    fontWeight: 500,
    position: 'sticky !important',
    left: 0,
    backgroundColor: colors.grey100,
    zIndex: 1,
  },
  dayHeader: {
    fontSize: '0.875rem',
    fontWeight: 600,
    position: 'relative',
  },
  scheduleCell: {
    width: '40px',
    height: '24px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    userSelect: 'none',
    '&:hover': {
      backgroundColor: colors.primary + '20',
    },
    '&.active': {
      backgroundColor: colors.primary + ' !important',
      color: 'white',
    },
    '&.inactive': {
      backgroundColor: 'transparent',
      border: `1px solid ${colors.grey300}`,
    },
    '&.dragging': {
      backgroundColor: colors.primary + '60',
    },
  },
  legend: {
    display: 'flex',
    gap: '1rem',
    marginTop: '0.5rem',
    alignItems: 'center',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
    fontSize: '0.75rem',
  },
  legendBox: {
    width: '12px',
    height: '12px',
    border: `1px solid ${colors.grey400}`,
  },
  granularityToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  sectionFooter: {
    display: 'flex',
    justifyContent: 'flex-start',
    marginTop: '1rem',
  },
  submitButton: {
    maxWidth: '200px',
  },
  caption: {
    fontSize: '.875rem',
    color: colors.grey800,
  },
}));

type Props = {
  schedule: boolean[][],
  onScheduleChange: (schedule: boolean[][]) => void,
  onSave: () => void,
  isSaving: boolean,
  enabled: boolean,
  onEnabledChange: (enabled: boolean) => void,
};

export default function WeeklyScheduleGrid({
  schedule,
  onScheduleChange,
  onSave,
  isSaving,
  enabled,
  onEnabledChange,
}: Props) {
  const classes = useStyles();
  const [timeFormat, setTimeFormat] = useState('24h');
  const [isDragging, setIsDragging] = useState(false);
  const [dragMode, setDragMode] = useState(null);

  const days = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
  const slotsPerDay = 24;

  const currentSchedule =
    schedule ||
    Array(7)
      .fill()
      .map(() => Array(slotsPerDay).fill(false));

  const getTimeLabel = slotIndex => {
    if (timeFormat === '24h') {
      return `${slotIndex.toString().padStart(2, '0')}:00`;
    } else {
      const hour =
        slotIndex === 0 ? 12 : slotIndex > 12 ? slotIndex - 12 : slotIndex;
      const ampm = slotIndex >= 12 ? 'PM' : 'AM';
      return `${hour}:00 ${ampm}`;
    }
  };

  const handlePointerDown = (dayIndex, slotIndex, event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!enabled) return;
    console.log('handlePointerDown', {dayIndex, slotIndex});

    setIsDragging(true);
    setDragMode(
      currentSchedule[dayIndex][slotIndex] ? 'deactivate' : 'activate'
    );

    const newSchedule = [...currentSchedule];
    newSchedule[dayIndex][slotIndex] = !newSchedule[dayIndex][slotIndex];
    onScheduleChange(newSchedule);
  };

  const handlePointerEnter = (dayIndex, slotIndex) => {
    if (isDragging && enabled) {
      const newSchedule = [...currentSchedule];
      newSchedule[dayIndex][slotIndex] = dragMode === 'activate';
      onScheduleChange(newSchedule);
    }
  };

  const handlePointerUp = () => {
    setIsDragging(false);
    setDragMode(null);
  };

  const applyPreset = preset => {
    if (!enabled) return;

    const newSchedule = Array(7)
      .fill()
      .map(() => Array(slotsPerDay).fill(false));

    switch (preset) {
      case 'weekdays_evening':
        for (let day = 0; day < 5; day++) {
          for (let slot = 19; slot < 24; slot++) {
            newSchedule[day][slot] = true;
          }
          for (let slot = 0; slot < 9; slot++) {
            newSchedule[day][slot] = true;
          }
        }
        break;
      case 'weekends_24_7':
        for (let day = 5; day < 7; day++) {
          for (let slot = 0; slot < slotsPerDay; slot++) {
            newSchedule[day][slot] = true;
          }
        }
        break;
      case 'clear_all':
        break;
      case 'invert':
        for (let day = 0; day < 7; day++) {
          for (let slot = 0; slot < slotsPerDay; slot++) {
            newSchedule[day][slot] = !currentSchedule[day][slot];
          }
        }
        break;
      default:
        break;
    }

    onScheduleChange(newSchedule);
  };

  useEffect(() => {
    document.addEventListener('pointerup', handlePointerUp);

    return () => {
      document.removeEventListener('pointerup', handlePointerUp);
    };
  }, []);

  return (
    <div className={classes.scheduleContainer}>
      <Box
        mb={2}
        p={2}
        style={{backgroundColor: colors.grey50, borderRadius: '8px'}}
      >
        <FormControlLabel
          control={
            <IosSwitch
              checked={enabled}
              onChange={e => onEnabledChange(e.target.checked)}
              color="primary"
            />
          }
          label={
            <Box>
              <Typography variant="body1" style={{fontWeight: 600}}>
                Horarios programados
              </Typography>
              <Typography variant="caption" className={classes.caption}>
                {enabled
                  ? 'El bot responderá solo en los horarios configurados'
                  : 'El bot estará activo 24/7 en todos los días de la semana'}
              </Typography>
            </Box>
          }
        />
      </Box>

      {enabled && (
        <>
          <Box display="flex" justifyContent="flex-start" alignItems="center">
            <div className={classes.granularityToggle}>
              <Typography variant="body1">24 Horas</Typography>
              <FormControlLabel
                control={
                  <IosSwitch
                    checked={timeFormat === '12h'}
                    onChange={e =>
                      setTimeFormat(e.target.checked ? '12h' : '24h')
                    }
                    color="primary"
                  />
                }
                label={'AM/PM'}
              />
            </div>
          </Box>

          <div className={classes.scheduleToolbar}>
            <Typography
              variant="body2"
              style={{
                fontWeight: 600,
                color: colors.grey700,
              }}
            >
              Configuraciones rápidas para horarios comunes
            </Typography>
            <ButtonGroup
              variant="outlined"
              size="small"
              className={classes.presetButtons}
              color="primary"
            >
              <Button
                startIcon={<AccessTime />}
                onClick={() => applyPreset('weekdays_evening')}
              >
                Lun-Vie 19:00-09:00
              </Button>
              <Button
                startIcon={<Schedule />}
                onClick={() => applyPreset('weekends_24_7')}
              >
                Fines de semana 24/7
              </Button>
              <Button
                startIcon={<Clear />}
                onClick={() => applyPreset('clear_all')}
              >
                Limpiar todo
              </Button>
              <Button
                startIcon={<SelectAll />}
                onClick={() => applyPreset('invert')}
              >
                Invertir selección
              </Button>
            </ButtonGroup>
          </div>

          <TableContainer className={classes.tableContainer}>
            <Table className={classes.scheduleTable} size="small">
              <TableHead>
                <TableRow>
                  <TableCell className={classes.timeCell}>Hora</TableCell>
                  {days.map((day, index) => (
                    <TableCell key={day} className={classes.dayHeader}>
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        {day}
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.from({length: slotsPerDay}, (_, slotIndex) => (
                  <TableRow key={slotIndex}>
                    <TableCell className={classes.timeCell}>
                      {getTimeLabel(slotIndex)}
                    </TableCell>
                    {days.map((day, dayIndex) => {
                      const isActive = currentSchedule[dayIndex][slotIndex];

                      return (
                        <TableCell
                          key={`${dayIndex}-${slotIndex}`}
                          className={classNames(
                            classes.scheduleCell,
                            isActive ? 'active' : 'inactive'
                          )}
                          onPointerDown={e =>
                            handlePointerDown(dayIndex, slotIndex, e)
                          }
                          onPointerEnter={() =>
                            handlePointerEnter(dayIndex, slotIndex)
                          }
                        />
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className={classes.legend}>
              <div className={classes.legendItem}>
                <div
                  className={classes.legendBox}
                  style={{backgroundColor: colors.primary}}
                />
                <Typography variant="caption">Activo</Typography>
              </div>
              <div className={classes.legendItem}>
                <div
                  className={classes.legendBox}
                  style={{backgroundColor: 'transparent'}}
                />
                <Typography variant="caption">Inactivo</Typography>
              </div>
            </div>
          </TableContainer>
        </>
      )}

      <footer className={classes.sectionFooter}>
        <CustomButton
          variant="contained"
          color="primary"
          className={classes.submitButton}
          onClick={onSave}
          disabled={isSaving}
          fullWidth
        >
          {isSaving ? 'Guardando...' : 'Guardar horarios'}
        </CustomButton>
      </footer>
    </div>
  );
}
